import { AssetReferralActionStatusEnum, Referral, Report, Roles } from "types";
import { ReportTabs } from "./AssetRecoveryAddReport";
import ReportList from "./AssetRecoveryReportList";
import { useAuth } from "context/AuthContext";
import { hasRole } from "utils/helper";
import { useState, useEffect, useMemo } from "react";
import {
  getAssetRecoveryReportsByReferralId,
  getReferralReportsByReferralId,
} from "services/assetRecoveryService";
import FileViewer from "components/file-components/fileViewer2";
import RespondantAssetAppealRequest from "components/pop-up/RespondantAssetAppealRequest";

export default function AssetRecoveryReportContainer({
  referralId,
  referral,
  setActiveTab,
  isAssetRegistrar,
}: {
  referralId: string;
  referral?: Referral;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  isAssetRegistrar: boolean;
}) {
  const { loginData } = useAuth();
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);

  const [reports, setReports] = useState<Report[]>([]);
  const [recoveryReports, setRecoveryReports] = useState<Report[]>([]);
  const [shouldShowReportTabs, setShouldShowReportTabs] = useState(false);

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);
  const [isRespondantPopupOpen, setIsRespondantPopupOpen] = useState(false);

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  useEffect(() => {
    getReferralReportsByReferralId(referralId)
      .then((d) => setReports(d))
      .catch(console.error);
    getAssetRecoveryReportsByReferralId(referralId)
      .then((d) => setRecoveryReports(d))
      .catch(console.error);
  }, [referralId]);

  const showReportTabStatuses = [
    AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_LITIGATION,
  ];

  const { showTabs, assetsNeedingReport, allProhibitedAssets } = useMemo(() => {
    // Get all prohibited assets
    const assetsForRecovery = (referral?.assets ?? []).filter(
      (a) => a.prohabition_court_verdict?.toLocaleLowerCase() === "prohabited"
    );

    if (!isAssetProsecutor) {
      return {
        showTabs: false,
        assetsNeedingReport: [],
        allProhibitedAssets: assetsForRecovery,
      };
    }

    // Get ALL recovery reports (both daily and final)
    const allRecoveryReports = recoveryReports;

    // Get all asset IDs that are already covered by ANY recovery report
    const coveredAssetIds = new Set();
    allRecoveryReports.forEach((report) => {
      if (report.asset) {
        // If asset is an object with id property
        if (report.asset.id) {
          coveredAssetIds.add(report.asset.id);
        }
        // If asset is an array of objects (backward compatibility)
        else if (Array.isArray(report.asset)) {
          report.asset.forEach((assetObj) => {
            if (assetObj.id) {
              coveredAssetIds.add(assetObj.id);
            }
          });
        }
      }
    });

    // Filter out assets that are already covered by ANY recovery report
    const needing = assetsForRecovery.filter(
      (asset) => !coveredAssetIds.has(asset.id)
    );

    const isRecoveryCase =
      referral?.action_status ===
      AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_LITIGATION;

    let show = false;

    if (isRecoveryCase) {
      show = true;
    } else {
      // For other cases, use the recovery-specific logic
      const hasAssetsForRecovery = assetsForRecovery.length > 0;
      const hasAssetsNeedingReport = needing.length > 0;
      show = hasAssetsForRecovery || hasAssetsNeedingReport;
    }

    return {
      showTabs: show,
      assetsNeedingReport: needing,
      allProhibitedAssets: assetsForRecovery,
    };
  }, [referral, recoveryReports, isAssetProsecutor]);

  useEffect(() => {
    setShouldShowReportTabs(showTabs);
  }, [showTabs]);

  const handleReportDataUpdated = async () => {
    const [proh, rec] = await Promise.all([
      getReferralReportsByReferralId(referralId),
      getAssetRecoveryReportsByReferralId(referralId),
    ]);
    setReports(proh);
    setRecoveryReports(rec);
  };

  return (
    <div className="flex gap-3">
      <ReportList
        referralId={referralId}
        fileRowProps={fileRowProps}
        reports={reports}
        recoveryReports={recoveryReports}
        handleReportDataUpdated={handleReportDataUpdated}
      />
      <div className="flex flex-col">
        {isAssetRegistrar &&
          recoveryReports.some(
            (report) => report.is_next_appointment_reason_final_verdict
          ) && (
            <div className="flex justify-end self-end mt-5">
              <button
                type="submit"
                className="referral-form-button text-white px-8 py-3"
                onClick={() => {
                  setIsRespondantPopupOpen(true);
                }}
              >
                Request Appeal
              </button>
            </div>
          )}
        {shouldShowReportTabs &&
          isAssetProsecutor &&
          showReportTabStatuses.includes(
            referral?.action_status as AssetReferralActionStatusEnum
          ) && (
            <ReportTabs
              referralId={referralId}
              assets={referral?.assets}
              assetsNeedingReport={assetsNeedingReport}
              setActiveTab={setActiveTab}
              onNewDataAdded={handleReportDataUpdated}
              referral={referral as Referral}
              recoveryReports={recoveryReports as Report[]}
              reports={reports as Report[]}
            />
          )}
      </div>

      <FileViewer
        selectedApplicantDocument={selectedDocument ?? undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
      <RespondantAssetAppealRequest
        isOpen={isRespondantPopupOpen}
        onClose={() => setIsRespondantPopupOpen(false)}
        asset={allProhibitedAssets}
        prohabition_request_court_registration_number={
          referral?.prohabition_request_court_registration_number as string
        }
        asset_referral={referralId}
        onSuccess={handleReportDataUpdated}
      />
    </div>
  );
}
